import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MapPin, Loader2, Building2, CalendarDays, Search } from "lucide-react";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";

const providerStatusColors = {
  ACTIVE: "bg-green-100 text-green-800", PENDING_APPROVAL: "bg-amber-100 text-amber-800",
  SUSPENDED: "bg-red-100 text-red-800", Unknown: "bg-slate-100 text-slate-600",
};
const sportColors = { basketball: "sport-basketball", badminton: "sport-badminton", pickleball: "sport-pickleball" };

export default function AdminVenues() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [listedFilter, setListedFilter] = useState("all");
  const [providerStatusFilter, setProviderStatusFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/venues").then(r => { setVenues(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const providerStatuses = Array.from(new Set(venues.map((venue) => venue.provider_status).filter(Boolean))).sort();
  const filteredVenues = useMemo(() => venues.filter((venue) => {
    const matchesSearch = !searchQuery || [venue.name, venue.city, venue.address, venue.provider_name].filter(Boolean).join(" ").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesListed = listedFilter === "all" || (listedFilter === "listed" ? venue.is_public_listed : !venue.is_public_listed);
    const matchesProviderStatus = providerStatusFilter === "all" || venue.provider_status === providerStatusFilter;
    return matchesSearch && matchesListed && matchesProviderStatus;
  }), [venues, searchQuery, listedFilter, providerStatusFilter]);
  const activeFilters = [
    searchQuery ? { key: "search", label: `Search: ${searchQuery}`, onRemove: () => setSearchQuery("") } : null,
    listedFilter !== "all" ? { key: "listed", label: listedFilter === "listed" ? "Listed" : "Unlisted", onRemove: () => setListedFilter("all") } : null,
    providerStatusFilter !== "all" ? { key: "provider-status", label: `Provider: ${providerStatusFilter}`, onRemove: () => setProviderStatusFilter("all") } : null,
  ].filter(Boolean);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="admin-venues-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-1">
        <MapPin className="w-8 h-8 text-court-orange" />All Venues
      </h1>
      <p className="text-muted-foreground mb-6">Platform-wide venue directory ({filteredVenues.length} shown)</p>

      <AdminFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by venue, provider, city or address"
        searchTestId="venue-search-input"
        filters={[
          { key: "listed", label: "Listed", value: listedFilter, onChange: setListedFilter, options: [{ value: "all", label: "All Listing States" }, { value: "listed", label: "Listed" }, { value: "unlisted", label: "Unlisted" }], testId: "venue-listed-filter" },
          { key: "provider-status", label: "Provider Status", value: providerStatusFilter, onChange: setProviderStatusFilter, options: [{ value: "all", label: "All Provider Statuses" }, ...providerStatuses.map((status) => ({ value: status, label: status }))], testId: "venue-provider-status-filter" },
        ]}
        activeFilters={activeFilters}
        onClearAll={() => { setSearchQuery(""); setListedFilter("all"); setProviderStatusFilter("all"); }}
        extraActions={<Button variant="outline" className="rounded-md" data-testid="venue-refine-btn"><Search className="w-4 h-4 mr-2" />Refine</Button>}
      />

      {filteredVenues.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No venues on platform</p>
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-100 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading uppercase tracking-wider text-xs">Venue</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Location</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Sports</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Provider</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs text-center">Courts</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs text-center">Bookings</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Listed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVenues.map(v => (
                <TableRow key={v.venue_id} data-testid={`admin-venue-${v.venue_id}`}>
                  <TableCell>
                    <p className="font-medium">{v.name}</p>
                    <p className="text-[10px] text-muted-foreground">{v.venue_id?.slice(0, 16)}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm flex items-center gap-1"><MapPin className="w-3 h-3" />{v.city}</p>
                    <p className="text-[10px] text-muted-foreground">{v.address}</p>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {v.sport_types?.map(s => (
                        <Badge key={s} variant="outline" className={`text-[10px] font-heading uppercase ${sportColors[s] || ''}`}>{s}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{v.provider_name}</p>
                    <Badge className={`${providerStatusColors[v.provider_status] || 'bg-slate-100'} text-[10px] font-heading uppercase mt-0.5`}>
                      {v.provider_status?.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center font-heading font-bold">{v.resources_count}</TableCell>
                  <TableCell className="text-center">
                    <span className="flex items-center justify-center gap-1 font-heading font-bold">
                      <CalendarDays className="w-3 h-3 text-muted-foreground" />{v.bookings_count}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] font-heading uppercase ${v.is_public_listed ? 'text-green-600 border-green-200' : 'text-slate-500 border-slate-200'}`}>
                      {v.is_public_listed ? "Public" : "Unlisted"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
